import { useState } from "react";
import { toast } from "sonner";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { GlassCard } from "@/components/GlassCard";
import { Reveal, SectionKicker } from "@/components/Reveal";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { container, goldBtn, inputCls, textareaCls, labelCls } from "@/lib/constants";

export default function Contact() {
  const [form, setForm] = useState({ full_name: "", organisation: "", role_title: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/public/contact", form);
      setSent(true);
      toast.success("Thank you. The desk will be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally {
      setSending(false);
    }
  };

  return (
    <PublicLayout>
      <SEO title="Contact" description="Speak to the desk. A minimal enquiry form for verified principals and their representatives." />
      <section className={`${container} py-20 lg:py-24`}>
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <SectionKicker>Speak to the desk</SectionKicker>
              <h1 className="pdg-serif text-4xl sm:text-5xl text-foreground leading-[1.08]">Contact</h1>
              <p className="mt-6 text-base text-foreground/80 leading-8 max-w-md">
                For membership enquiries and mandate discussions. We respond to verified principals
                and their representatives only.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <GlassCard className="p-8">
              {sent ? (
                <div className="py-12 text-center" data-testid="contact-success">
                  <h2 className="pdg-serif text-2xl text-foreground">Received.</h2>
                  <p className="mt-3 text-sm text-muted-foreground">The desk will be in touch discreetly.</p>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Full name</label>
                      <input required data-testid="contact-name-input" className={inputCls} value={form.full_name} onChange={set("full_name")} />
                    </div>
                    <div>
                      <label className={labelCls}>Organisation</label>
                      <input required data-testid="contact-org-input" className={inputCls} value={form.organisation} onChange={set("organisation")} />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className={labelCls}>Role</label>
                      <input required data-testid="contact-role-input" className={inputCls} value={form.role_title} onChange={set("role_title")} />
                    </div>
                    <div>
                      <label className={labelCls}>Email</label>
                      <input required type="email" data-testid="contact-email-input" className={inputCls} value={form.email} onChange={set("email")} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Message</label>
                    <textarea required rows={5} data-testid="contact-message-input" className={textareaCls} value={form.message} onChange={set("message")} />
                  </div>
                  <button type="submit" disabled={sending} data-testid="contact-submit-button" className={goldBtn}>
                    {sending ? "Sending…" : "Send enquiry"}
                  </button>
                </form>
              )}
            </GlassCard>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
